import { useState, useEffect, useRef } from 'react';
import { Trophy, Plus, Trash2, Upload, Pencil, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  getChampions,
  createChampion,
  updateChampion,
  deleteChampion,
  uploadChampionPhoto,
} from '../../lib/api';
import { toast } from 'sonner';

const emptyForm = { year: new Date().getFullYear() - 1, edition: '', team_name: '', notes: '' };

const ChampionDialog = ({ open, initial, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        year: initial.year,
        edition: initial.edition ?? '',
        team_name: initial.team_name || '',
        notes: initial.notes || '',
      } : emptyForm);
      setPhotoFile(null);
      if (initial?.photo_data) {
        setPhotoPreview(`data:${initial.photo_content_type};base64,${initial.photo_data}`);
      } else {
        setPhotoPreview(null);
      }
    }
  }, [open, initial]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type?.startsWith('image/')) {
      toast.error('Seleziona un\'immagine valida');
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 3MB)');
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    if (!form.year) {
      toast.error("L'anno è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        year: parseInt(form.year, 10),
        team_name: form.team_name.trim() || null,
        notes: form.notes.trim() || null,
        edition: form.edition === '' ? null : parseInt(form.edition, 10),
      };
      await onSave(payload, photoFile);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="champion-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Modifica vincitore' : 'Nuovo vincitore'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Photo dropzone / picker */}
          <label
            htmlFor="champ-photo-input"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            className="block border-2 border-dashed border-slate-700 hover:border-primary rounded-xl p-4 text-center cursor-pointer transition-colors"
          >
            {photoPreview ? (
              <img src={photoPreview} alt="anteprima" className="max-h-44 mx-auto rounded-lg object-contain" />
            ) : (
              <div className="text-slate-400 py-4">
                <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                <p className="text-sm">Trascina un'immagine o clicca per caricarla</p>
                <p className="text-xs text-slate-500 mt-1">Max 3MB · facoltativa in modifica</p>
              </div>
            )}
            <input
              id="champ-photo-input"
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              data-testid="champion-photo-input"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Anno *</label>
              <Input
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="champion-year-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Numero edizione</label>
              <Input
                type="number"
                placeholder="es. 29"
                value={form.edition}
                onChange={(e) => setForm({ ...form, edition: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="champion-edition-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Squadra vincitrice (facoltativo)</label>
            <Input
              placeholder="Lascia vuoto se non lo ricordi"
              value={form.team_name}
              onChange={(e) => setForm({ ...form, team_name: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="champion-team-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Note (opzionale)</label>
            <Input
              placeholder="es. battuta in finale ai rigori"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-800 border-slate-700"
              data-testid="champion-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-slate-950 hover:bg-primary/90"
            data-testid="save-champion-btn"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ChampionRow = ({ champion, onPhoto, onEdit, onDelete }) => {
  const [dragOver, setDragOver] = useState(false);
  const ref = useRef(null);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onPhoto(champion.id, file);
    else toast.error('Trascina un\'immagine valida');
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`bg-slate-900 border rounded-xl p-4 flex items-center gap-4 transition-all ${
        dragOver ? 'border-gold border-dashed bg-gold/5 scale-[1.01]' : 'border-slate-800'
      }`}
      data-testid={`champion-row-${champion.id}`}
    >
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-20 h-20 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-gold/50 transition-all"
        title="Carica/trascina foto"
      >
        {champion.photo_data ? (
          <img
            src={`data:${champion.photo_content_type};base64,${champion.photo_data}`}
            alt={champion.team_name || `Vincitore ${champion.year}`}
            className="w-full h-full object-cover"
          />
        ) : dragOver ? (
          <ImageIcon className="w-7 h-7 text-gold animate-pulse" />
        ) : (
          <Trophy className="w-7 h-7 text-slate-600" />
        )}
        <input
          ref={ref}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) onPhoto(champion.id, e.target.files[0]);
            e.target.value = '';
          }}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-numbers font-bold text-gold">{champion.year}</span>
          {champion.edition ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
              {champion.edition}ª ed.
            </span>
          ) : null}
        </div>
        {champion.team_name ? (
          <div className="font-heading font-bold truncate">{champion.team_name}</div>
        ) : (
          <div className="text-sm italic text-slate-500">Squadra non specificata</div>
        )}
        {champion.notes ? <div className="text-xs text-slate-400 truncate">{champion.notes}</div> : null}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onEdit(champion)}
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-primary transition-colors"
          title="Modifica"
          data-testid={`edit-champion-${champion.id}`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => ref.current?.click()}
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
          title="Carica foto"
        >
          <Upload className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(champion)}
          className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
          data-testid={`delete-champion-${champion.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const AdminAlboDoroPage = () => {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await getChampions();
      setChampions(res.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleSave = async (payload, photoFile) => {
    try {
      if (editing) {
        await updateChampion(editing.id, payload);
        if (photoFile) await uploadChampionPhoto(editing.id, photoFile);
        toast.success('Vincitore aggiornato');
      } else {
        const res = await createChampion(payload);
        if (photoFile && res.data?.id) {
          await uploadChampionPhoto(res.data.id, photoFile);
        }
        toast.success('Vincitore aggiunto');
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      throw e;
    }
  };

  const handleDelete = async (c) => {
    const label = c.team_name ? `"${c.team_name}" (${c.year})` : `del ${c.year}`;
    if (!window.confirm(`Eliminare il vincitore ${label}?`)) return;
    try {
      await deleteChampion(c.id);
      toast.success('Eliminato');
      fetchAll();
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handlePhoto = async (id, file) => {
    try {
      await uploadChampionPhoto(id, file);
      toast.success('Foto caricata');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 min-h-screen" data-testid="admin-albo-doro-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              <Trophy className="w-7 h-7 text-gold" />
              Albo d'Oro
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {champions.length} edizion{champions.length === 1 ? 'e' : 'i'} · ordinati per anno (più recenti prima)
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-primary text-slate-950 hover:bg-primary/90"
            data-testid="add-champion-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi vincitore
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-400">Caricamento...</p>
        ) : champions.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-xl text-slate-400">
            <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p>Nessun vincitore aggiunto.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {champions.map(c => (
              <ChampionRow
                key={c.id}
                champion={c}
                onPhoto={handlePhoto}
                onEdit={(c2) => { setEditing(c2); setDialogOpen(true); }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
      <ChampionDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
    </div>
  );
};

export default AdminAlboDoroPage;
